import CloseIcon from "@assets/svg/CloseIcon";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { inviteUserViaEmail } from "@endpoints/recruiter.service";
import { colors } from "@utils/theme";
import { useSnackbar } from "notistack";
import { Controller, useForm } from "react-hook-form";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from "@components/ui/dialog";
import { Dispatch, SetStateAction, useState } from "react";
import CustomInput from "@components/CustomInput/CustomInput";
import { updateReferralAccountApi } from "@endpoints/referral.service";
import { useSelector } from "react-redux";
import { RootState } from "@store/reducers";
import { EnumUserTypes } from "@utils/constants";

interface InviteReferralMemberModalProps {
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
}

const InviteReferralMemberModal = ({
  openModal,
  setOpenModal,
}: InviteReferralMemberModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth?.user);
  const {
    control,
    formState: { errors },
    watch,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      firstName: undefined,
      lastName: undefined,
      email: undefined,
      recipientText: undefined,
    },
  });
  const fields = watch();

  const [isInviting, setIsInviting] = useState(false);

  const handleFormSubmit = async () => {
    try {
      setIsInviting(true);
      if (user?.userType === EnumUserTypes.REFERRAL_AMBASSADOR) {
        await inviteUserViaEmail(fields);
      } else {
        await updateReferralAccountApi(fields);
      }
      enqueueSnackbar("Member invited successfully", { variant: "success" });
      setOpenModal(false);
      reset({
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        recipientText: undefined,
      });
    } catch (error) {
      enqueueSnackbar("Error inviting member", { variant: "error" });
    } finally {
      setIsInviting(false);
    }
  };
  return (
    <Dialog open={openModal} onOpenChange={setOpenModal}>
      <DialogContent showCloseButton={false} className="bg-white">
        <div className="flex justify-between mb-5">
          <div className="flex flex-col">
            <span className="font-semibold text-lg">Invite member</span>
            <span className="font-medium text-xs text-light-black">
              Expand your team by adding members
            </span>
          </div>
          <DialogClose asChild>
            <button
              onClick={() => {
                setOpenModal(false);
                reset({
                  firstName: undefined,
                  lastName: undefined,
                  email: undefined,
                });
              }}
              className="h-max p-2 rounded-md hover:bg-gray-50"
            >
              <CloseIcon className={"w-5 h-5"} />
            </button>
          </DialogClose>
        </div>
        <div className="bg-[#F9FAFB] p-5 rounded-md flex flex-col gap-3">
          <div className="flex items-center gap-5">
            {/* @ts-ignore */}
            <CustomInput
              label="First Name"
              name="firstName"
              placeholder={"Enter first name"}
              control={control}
              errors={errors}
              rules={{
                required: "This field is required.",
              }}
            />
            {/* @ts-ignore */}
            <CustomInput
              label="Last Name"
              placeholder={"Enter last name"}
              name="lastName"
              control={control}
              errors={errors}
              rules={{
                required: "This field is required.",
              }}
            />
          </div>
          <div>
            {/* @ts-ignore */}
            <CustomInput
              label="Email address"
              placeholder={"Enter recipient's email address"}
              name="email"
              control={control}
              errors={errors}
              rules={{
                required: "Email is required",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/,
                  message: "Invalid email address",
                },
              }}
            />
          </div>
          <div>
            <Controller
              name="recipientText"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div className="flex flex-col gap-2">
                    <span className="text-primary text-xs font-medium">
                      Add a text for your recipient{" "}
                      <span className="text-red-500">*</span>
                    </span>
                    <textarea
                      {...field}
                      rows={4}
                      maxLength={200}
                      placeholder="Add a comment so the recipient will know this invite is really from you."
                      className="bg-white rounded-md border p-2.5 text-xs font-medium outline-none"
                    />

                    <div className="flex items-center justify-between">
                      {fieldState?.error ? (
                        <span className="whitespace-pre text-xs mt-2 text-orange-700">
                          {fieldState?.error?.message}
                        </span>
                      ) : (
                        <div></div>
                      )}
                      <span className="text-xs font-medium text-right text-light-black">
                        {field?.value?.length || 0}/200
                      </span>
                    </div>
                  </div>
                );
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={handleSubmit(handleFormSubmit)}
            className="font-bold text-sm bg-primary text-white py-1.5 px-5 rounded-md flex items-center justify-self-end mt-5"
          >
            {isInviting && <LoadingAnimation color={colors.White} />} Invite
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteReferralMemberModal;
